import { canNormalizeSchemaParam } from 'Pods/ThreeDStudio/Services/canNormalizeSchemaParam'
import { getCurrentValueForSchemaParam } from 'Pods/ThreeDStudio/Services/getCurrentValueForSchemaParam'
import { getDefaultStorePatchForSchemaParam } from 'Pods/ThreeDStudio/Services/getDefaultStorePatchForSchemaParam'
import { isSchemaValueInvalid } from 'Pods/ThreeDStudio/Services/isSchemaValueInvalid'
import { getEnhancedSettingsKey } from 'Pods/ThreeDStudio/Shared/Utils/getEnhancedSettingsKey'

import { threeDStudioStore } from '../index'

const setSchemaState = (patch) => {
  threeDStudioStore.setState((state) => ({
    schema: { ...state.schema, ...patch },
  }))
}

/**
 * Sets the generation action
 * @param {string} generationAction - One of: "image-to-3d", "text-to-3d", "conversion",
 *   "mesh-segmentation", "mesh-simplification", "retexture", "uv-unfolding"
 */
export const setGenerationAction = (generationAction) => {
  setSchemaState({ generationAction })
}

/**
 * Sets the generation provider
 * @param {string} generationProvider - One of: "tencent", "meshy", "hyper3d", "tripo3d"
 */
export const setGenerationProvider = (generationProvider) => {
  setSchemaState({ generationProvider })
}

/**
 * Sets the list of providers available for the current generation action and
 * keeps the active generation provider in sync:
 *
 * - If only one provider is available, it becomes active.
 * - If multiple are available, the currently active provider is kept when still
 *   in the list; otherwise the first provider is activated.
 * - If none are available, the active provider is cleared.
 *
 * @param {string[]} providers - Unique list of provider ids derived from the
 *   schemas matching the current generation action.
 */
export const setAvailableGenerationProviders = (providers) => {
  const { schema } = threeDStudioStore.getState()
  const currentProviders = schema.availableGenerationProviders || []
  const currentActive = schema.generationProvider
  const nextActive = (() => {
    if (providers.length === 0) {
      return undefined
    }
    if (providers.length === 1) {
      return providers[0]
    }
    return providers.includes(currentActive) ? currentActive : providers[0]
  })()

  const arraysAreEqual = providers.length === currentProviders.length && providers.every(
    (value, index) => value === currentProviders[index]
  )

  const patch = {}
  if (!arraysAreEqual) {
    patch.availableGenerationProviders = providers
  }
  if (nextActive !== currentActive) {
    patch.generationProvider = nextActive
  }

  if (Object.keys(patch).length > 0) {
    setSchemaState(patch)
  }
}

/**
 * Sets the list of providers available for the current enhanced action and
 * keeps the active enhanced provider in sync, using the same 0/1/many rules
 * as setAvailableGenerationProviders.
 *
 * @param {string[]} providers - Unique list of provider ids derived from the
 *   schemas matching the current enhanced action.
 */
export const setAvailableEnhancedProviders = (providers) => {
  const { schema } = threeDStudioStore.getState()
  const currentProviders = schema.availableEnhancedProviders || []
  const currentActive = schema.enhancedProvider
  const nextActive = (() => {
    if (providers.length === 0) {
      return undefined
    }
    if (providers.length === 1) {
      return providers[0]
    }
    return providers.includes(currentActive) ? currentActive : providers[0]
  })()

  const arraysAreEqual = providers.length === currentProviders.length && providers.every(
    (value, index) => value === currentProviders[index]
  )

  const patch = {}
  if (!arraysAreEqual) {
    patch.availableEnhancedProviders = providers
  }
  if (nextActive !== currentActive) {
    patch.enhancedProvider = nextActive
  }

  if (Object.keys(patch).length > 0) {
    setSchemaState(patch)
  }
}

/**
 * Sets the generation model schema and normalizes store values that are invalid for the new schema
 * (resets them to input_schema default via schemaToStoreConfig).
 * @param {Object} generationModelSchema - The generation model schema object
 */
export const setGenerationModelSchema = (generationModelSchema) => {
  threeDStudioStore.setState((state) => {
    const nextSchema = { ...state.schema, generationModelSchema }
    if (generationModelSchema == null) {
      return { ...state, schema: nextSchema }
    }

    const properties = generationModelSchema.input_schema?.properties || {}
    const nextModelSettings = { ...state.modelSettings }
    Object.entries(properties).forEach(([schemaKey, propSchema]) => {
      if (propSchema?.default === undefined || !canNormalizeSchemaParam(schemaKey)) {
        return
      }

      const uiSchema = generationModelSchema.ui_schema?.[schemaKey] || {}
      const disabledValues = uiSchema?.['ui:options']?.disabled_values || []
      const currentValue = getCurrentValueForSchemaParam(schemaKey, 'modelSettings', state)
      if (
        !isSchemaValueInvalid(currentValue, propSchema, disabledValues)
      ) {
        return
      }

      const patch = getDefaultStorePatchForSchemaParam(schemaKey, propSchema, state.modelSettings)
      if (patch) {
        Object.assign(nextModelSettings, patch)
      }
    })

    return { ...state, schema: nextSchema, modelSettings: nextModelSettings }
  })
}

/**
 * Sets the enhanced action
 * @param {string} enhancedAction - One of: "image-to-3d", "text-to-3d", "conversion",
 *   "mesh-segmentation", "mesh-simplification", "retexture", "uv-unfolding"
 */
export const setEnhancedAction = (enhancedAction) => {
  setSchemaState({ enhancedAction })
}

/**
 * Sets the enhanced provider
 * @param {string} enhancedProvider - One of: "tencent", "meshy", "hyper3d", "tripo3d"
 */
export const setEnhancedProvider = (enhancedProvider) => {
  setSchemaState({ enhancedProvider })
}

/**
 * Sets the enhanced mode
 * @param {string} enhancedMode - One of: "normal", "fast"
 */
export const setEnhancedMode = (enhancedMode) => {
  setSchemaState({ enhancedMode })
}

/**
 * Sets the enhanced model schema and normalizes store values that are invalid for the new schema
 * (resets them to input_schema default via schemaToStoreConfig).
 * @param {Object} enhancedModelSchema - The enhanced model schema object
 */
export const setEnhancedModelSchema = (enhancedModelSchema) => {
  threeDStudioStore.setState((state) => {
    const nextSchema = { ...state.schema, enhancedModelSchema }
    const activeViewerSidebarTab = state.ui.activeViewerSidebarTab
    if (enhancedModelSchema == null || !activeViewerSidebarTab) {
      return { ...state, schema: nextSchema }
    }

    const settingsKey = getEnhancedSettingsKey(activeViewerSidebarTab, state.schema.enhancedAction)
    if (!settingsKey) {
      return { ...state, schema: nextSchema }
    }

    const properties = enhancedModelSchema.input_schema?.properties || {}
    const nextSettings = { ...state[settingsKey] }
    Object.entries(properties).forEach(([schemaKey, propSchema]) => {
      if (propSchema?.default === undefined || !canNormalizeSchemaParam(schemaKey)) {
        return
      }

      const uiSchema = enhancedModelSchema.ui_schema?.[schemaKey] || {}
      const disabledValues = uiSchema?.['ui:options']?.disabled_values || []
      const currentValue = getCurrentValueForSchemaParam(schemaKey, settingsKey, state)
      if (
        !isSchemaValueInvalid(currentValue, propSchema, disabledValues)
      ) {
        return
      }

      const patch = getDefaultStorePatchForSchemaParam(schemaKey, propSchema, state[settingsKey])
      if (patch) {
        Object.assign(nextSettings, patch)
      }
    })

    return { ...state, schema: nextSchema, [settingsKey]: nextSettings }
  })
}
